<template>
  <v-container>
    <v-row align="center" class="ml-2 mr-2">
      <v-col cols="12" align="center">
        <img placeholder="Senior" style="width: 120px; margin-right: 10px; margin-top: 10px;" src="@/assets/icone-senior-connect_colorido-solo.svg" />
        <h1 class="light cabecalho" style="color: #009280">Senior Connect</h1>
      </v-col>
    </v-row>

    <v-row>
      <v-col
        xs="12"
        sm="8"
        md="8"
        lg="6"
        xl="4"
        offset-sm="2"
        offset-md="2"
        offset-lg="3"
        offset-xl="4"
      >
        <v-card>
          <v-card-title>
            <h3 class="light">{{$t('keywords.select_client')}}</h3>
          </v-card-title>
          <v-card-text>
            <v-form v-model="valid">
              <v-autocomplete
                :items="clientList"
                :label="$t('select_client.client')"
                name="client"
                item-text="label"
                item-value="value"
                v-model="client"
                clearable
                dense
              ></v-autocomplete>
            </v-form>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              :disabled="!valid || client === null"
              color="#009280"
              class="white--text"
              @click="done"
            >{{$t('select_client.select')}}</v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
    <v-overlay v-model="loading">
      <v-progress-circular indeterminate />
    </v-overlay>
    <v-dialog v-model="showRedirectDialog" persistent width="500">
      <v-card>
        <v-card-title class="redirect-dialog-title">
          {{ $t('select_client.redirect_dialog.title') }}
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="redirect-dialog-body">
          {{ $t('select_client.redirect_dialog.body') }}
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="secundary" v-if="hasOnpremiseClients" @click="showRedirectDialog = false" outlined>
            {{ $t('button.close') }}
          </v-btn>
          <v-btn color="primary" @click="redirect">
            {{ $t('select_client.redirect_dialog.button') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<style>
.light {
  font-weight: 300;
}
.cabecalho {
  margin-top: 45px;
  display: inline-block;
  vertical-align: top;
}
.redirect-dialog-title {
  display: flex;
  flex-direction: column;
  color: #009280;
  overflow-y: auto;
  min-height: 5vh;
}
.redirect-dialog-body {
  padding: 15px 20px !important;
  overflow-y: auto;
}
</style>

<script>
import ClientService from '../../services/ClientService'
import LocalStorageService from '../../services/LocalStorageService'
/* eslint-disable indent */

/**
 * This function will do a 'await' in all promises but won't throw any exception if has some error
 * @param {Promise[]} promises
 */
async function awaitAll (promises) {
  for (const prom of promises) {
    try { await prom } catch (_) {}
  }
}

/**
 * @typedef {Object} Client It's a basic type of client
 * @prop {string} urlPrefix
 * @prop {string} clientPrefix
 * @prop {string} accessUrl
 * @prop {string} clientType
 */
export default {
  props: {
    listFrom: String
  },

  data: () => ({
    loading: false,
    showRedirectDialog: false,
    hasOnpremiseClients: false,
    clientList: [],
    client: null,
    /** @type {Client} */
    parentClient: null,
    valid: false
  }),

  methods: {

    redirect () {
      window.location.href = 'https://xplatform.seniorconnect.com.br/user-password/migrate-user'
    },

    /**
     * This method will return all children clients of a parent client
     * @param {string} accessUrl accessUrl of parent client
     * @param {string} clientPrefix clientPrefix of parent client
     * @returns {Promise<Client[]>}
     */
    async findAll (accessUrl, clientPrefix) {
      const response = await ClientService.findAll(accessUrl, clientPrefix)
      return response.data
    },

    /**
     * Will 'iterate' each level of hierarchy to find a client
     * @param {string} urlPrefix urlPrefix of the client that you wanna find
     * @param {Client[]} parents parents where start the search for the children client
     * @returns {Promise<Client>}
     */
    async findClient (urlPrefix, parents) {
      /** @type {Client[]} */
      const allSubClients = [] // Store all found clients
      /** @type {Promise[]} */
      const requests = [] // Store all requests to findAll children clients

      for (const { urlPrefix, accessUrl } of parents) {
        const request = this.findAll(accessUrl, urlPrefix).then((result) => allSubClients.push(...result))
        requests.push(request)
      }
      await awaitAll(requests) // Await all children clients

      const found = allSubClients.find(client => client.urlPrefix === urlPrefix)
      if (found) return found

      if (allSubClients.length === 0) throw new Error("Can't find this urlPrefix inside the hierarchy")

      // Tail recursion to find the client entering each hierarchy level
      const nextParents = allSubClients.filter(c => c.clientType === 'ROOT' && !parents.some(p => p.urlPrefix === c.urlPrefix))
      return this.findClient(urlPrefix, nextParents)
    },

    /**
     * Return the self-reference of a client
     * @param {Client} client
     * @returns {Promise<Client>}
     */
    async findSelfReference (client) {
      try {
        const children = await this.findAll(client.accessUrl, client.urlPrefix)
        const found = children.find(_client => _client.urlPrefix === client.urlPrefix)
        if (!found) throw new Error()
        return found
      } catch (_) {
        throw new Error("Can't found client self-reference")
      }
    },

    /**
     * Return the client where the user was created
     * @returns {Promise<Client>}
     */
    async findLoginClient () {
      /** @type {Client} */
      const xplatformClient = {
        urlPrefix: 'xplatform',
        clientName: 'XPlatform',
        charterCode: '',
        clientType: 'ROOT',
        contactAddress: null,
        cutDate: null,
        active: true,
        token: 'xplatform',
        platform: true,
        accessUrl: 'https://seniorconnect.com.br:9010',
        clientPrefix: 'xplatform',
        createdAt: null,
        lastModified: '2023-07-26T13:09:52',
        lastSync: null,
        lastModifiedBy: 'system',
        routingkey: 'enterprise'
      }
      const userClientPrefix = LocalStorageService.getUserClientPrefix()
      if (userClientPrefix === xplatformClient.urlPrefix) return xplatformClient

      try {
        return await this.findClient(userClientPrefix, [xplatformClient])
      } catch (error) {
        this.$message.error(error)
        return null
      }
    },

    async getClients () {
      this.loading = true
      this.parentClient = this.listFrom
                          ? (await ClientService.findById(this.listFrom)).data
                          : await this.findLoginClient()
      const children = await this.findAll(this.parentClient.accessUrl, this.parentClient.urlPrefix)
      const selfReference = children.find(c => c.urlPrefix === this.parentClient.urlPrefix)
      this.clientList = children
                          .filter(c => c.accessUrl !== 'https://seniorconnect.com.br:9013/api')
                          .map(client => ({ label: client.clientName, value: client }))
      this.loading = false

      this.showRedirectDialog = selfReference?.accessUrl === 'https://seniorconnect.com.br:9013/api'
      this.hasOnpremiseClients = this.clientList.find(c => c.accessUrl !== 'https://seniorconnect.com.br:9013/api')
    },

    done () {
      LocalStorageService.setClientPrefix(this.client.urlPrefix)
      LocalStorageService.setClientName(this.client.clientName)

      const useParentAccessUrl = this.client.accessUrl === this.client.urlPrefix && this.client.urlPrefix === this.parentClient.urlPrefix
      if (useParentAccessUrl) LocalStorageService.setAccessUrl(this.parentClient.accessUrl)
      else LocalStorageService.setAccessUrl(this.client.accessUrl)

      this.$versionable.update()
      this.$router.push('/')
    }
  },

  created () {
    this.getClients()
  }
}
</script>
